import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Download, Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { parseErrorMessage } from "@/lib/queryClient";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PulseFeedbackResponse } from "./types";
import { csvSafeText, downloadCsv, rowsToCsv } from "./helpers";

export function PulseFeedbackPanel() {
  const [voteFilter, setVoteFilter] = useState<"all" | "up" | "down">("all");

  const feedbackQuery = useQuery<PulseFeedbackResponse>({
    queryKey: ["/api/admin/pulse/feedback", voteFilter],
    queryFn: async () => {
      const qs = voteFilter === "all" ? "" : `?vote=${voteFilter}`;
      const res = await fetch(`/api/admin/pulse/feedback${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Pulse feedback");
      return res.json();
    },
  });

  const data = feedbackQuery.data;
  const entries = data?.entries ?? [];

  const handleDownloadCsv = () => {
    const rows: Array<Array<unknown>> = [];
    rows.push(["Vote", "User", "Email", "Item Key", "Note", "Timestamp"]);
    for (const e of entries) {
      rows.push([
        e.vote,
        csvSafeText(e.name ?? ""),
        csvSafeText(e.email ?? ""),
        csvSafeText(e.itemKey),
        csvSafeText(e.note ?? ""),
        e.at,
      ]);
    }
    const filterSlug = voteFilter === "all" ? "all" : voteFilter;
    downloadCsv(`pulse-feedback-${filterSlug}.csv`, rowsToCsv(rows));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-pulse-feedback-title">
            Pulse Feedback (AI Training Review)
          </h3>
          <p className="text-sm text-muted-foreground">
            Collected 👍/👎 votes hosts left on Pulse briefing items, with their optional notes.
            Use the positive signal to reinforce good briefings and the negative signal to correct
            bad ones.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadCsv}
            disabled={entries.length === 0}
            data-testid="button-download-pulse-feedback-csv"
          >
            <Download className="h-4 w-4 mr-2" />
            Download CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => feedbackQuery.refetch()}
            disabled={feedbackQuery.isFetching}
            data-testid="button-refresh-pulse-feedback"
          >
            {feedbackQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {(["all", "up", "down"] as const).map((v) => (
          <Button
            key={v}
            size="sm"
            variant={voteFilter === v ? "default" : "outline"}
            onClick={() => setVoteFilter(v)}
            data-testid={`button-pulse-feedback-filter-${v}`}
          >
            {v === "all" ? "All" : v === "up" ? "👍 Up" : "👎 Down"}
          </Button>
        ))}
      </div>

      {feedbackQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : feedbackQuery.error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600" data-testid="text-pulse-feedback-error">
            {parseErrorMessage(feedbackQuery.error, "Failed to load Pulse feedback")}
          </p>
        </Card>
      ) : entries.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground" data-testid="text-pulse-feedback-empty">
            No feedback collected yet{voteFilter !== "all" ? ` for ${voteFilter === "up" ? "👍 up" : "👎 down"} votes` : ""}.
          </p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2 text-sm text-muted-foreground border-b" data-testid="text-pulse-feedback-count">
            {entries.length.toLocaleString()} {entries.length === 1 ? "entry" : "entries"}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Vote</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Briefing Item</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="w-44">When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e, i) => (
                <TableRow key={`${e.userId}-${e.itemKey}-${i}`} data-testid={`row-pulse-feedback-${i}`}>
                  <TableCell data-testid={`text-pulse-feedback-vote-${i}`}>
                    {e.vote === "up" ? (
                      <span className="text-green-600 font-medium">👍 Up</span>
                    ) : (
                      <span className="text-red-600 font-medium">👎 Down</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm" data-testid={`text-pulse-feedback-user-${i}`}>
                    <div className="font-medium">{e.name || e.email || `User #${e.userId}`}</div>
                    {e.name && e.email ? (
                      <div className="text-xs text-muted-foreground">{e.email}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm" data-testid={`text-pulse-feedback-item-${i}`}>
                    {e.itemLabel ? (
                      <>
                        <div className="font-medium" data-testid={`text-pulse-feedback-itemlabel-${i}`}>
                          {e.itemLabel}
                        </div>
                        <div
                          className="text-xs font-mono text-muted-foreground break-all"
                          data-testid={`text-pulse-feedback-itemkey-${i}`}
                        >
                          {e.itemKey}
                        </div>
                      </>
                    ) : (
                      <span
                        className="font-mono break-all"
                        data-testid={`text-pulse-feedback-itemkey-${i}`}
                      >
                        {e.itemKey}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm whitespace-pre-wrap" data-testid={`text-pulse-feedback-note-${i}`}>
                    {e.note ? e.note : <span className="text-muted-foreground italic">—</span>}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums" data-testid={`text-pulse-feedback-at-${i}`}>
                    {(() => {
                      const t = Date.parse(e.at);
                      return Number.isFinite(t) ? new Date(t).toLocaleString() : e.at;
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
